// The member book page's week strip and day grid (X47; Grant 2026-10-19:
// "all the courts should take up the column headings but you should be able
// to page through each week at the top to select the day"). Pure shapes and
// words only, unit-tested without Vuetify: the week's days and paging within
// the viewer's window, one area group at a time (one slot step, so the rows
// are regular), the Mon–Sun strip with each day's free count, the rows
// (labelled on the hour), and the one cell model the grid and the legend both
// draw. The network half stays in composables/useBookingsMember.js (one call
// per week: GET availability?from=<Monday>&days=7).
//
// A cell's state comes from the API's per-cell status and per-slot `bookable`
// flag plus the viewer's own allowance (window, limit, membership, and X54:
// whether the club can take a card payment for a priced slot): a price is
// drawn ONLY on a cell this viewer can book right now, so a tap on it always
// opens the booking flow (Grant: "loads of plus icons but some of them don't
// work"). X54 (BOOK_REFINE_RULINGS 1–10, DESIGN_D2): tabs named by their
// contents, one block per booking and one band per run of a state
// (columnBlocks), the earlier rows of today collapsed (startedRows), a drag
// across free cells (rangeValidity, rangeLabel), prices at two decimals, the
// strip's day words per viewer, and the allowance as chips.
import {NAME_NOUNS, areaWords} from '~/utils/areaWords';
import {areasForSport, cellWord, cutoffLine, moneyExact} from '~/utils/bookingsMember';
import {createRange, durationWords} from '~/utils/bookingDrag';
import {clock24, clockWord, periodWord} from '~/utils/bookings';
import {slotRows} from '~/utils/bookingRows';
import {dayMeter} from '~/utils/bookingDayMeter';

export const WEEKDAYS = Object.freeze(['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']);
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// ── Dates (yyyy-MM-dd keys in the club's zone; no Date arithmetic in local time) ──
const parse = (date) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(date ?? ''));
  if (!m) return null;
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], 12));
  return Number.isNaN(d.getTime()) ? null : d;
};
const key = (d) => d.toISOString().slice(0, 10);

export function isDateKey(date) {
  return parse(date) != null;
}

export function addDays(date, n) {
  const d = parse(date);
  if (!d) return null;
  d.setUTCDate(d.getUTCDate() + n);
  return key(d);
}

/** The Monday of the week holding `date`. */
export function weekStart(date) {
  const d = parse(date);
  if (!d) return null;
  const dow = (d.getUTCDay() + 6) % 7; // Mon = 0
  d.setUTCDate(d.getUTCDate() - dow);
  return key(d);
}

/** The seven day keys from a Monday. */
export function weekDays(monday) {
  const start = weekStart(monday);
  if (!start) return [];
  return Array.from({length: 7}, (_, i) => addDays(start, i));
}

/** "19–25 Oct" / "26 Oct – 1 Nov" / "28 Dec – 3 Jan 2027" (the year only when the week crosses one). */
export function weekLabel(monday) {
  const a = parse(weekStart(monday));
  if (!a) return '';
  const b = parse(addDays(key(a), 6));
  const dayA = a.getUTCDate(), dayB = b.getUTCDate();
  const monA = MONTHS[a.getUTCMonth()], monB = MONTHS[b.getUTCMonth()];
  if (monA === monB) return `${dayA}–${dayB} ${monA}`;
  const year = a.getUTCFullYear() !== b.getUTCFullYear() ? ` ${b.getUTCFullYear()}` : '';
  return `${dayA} ${monA} – ${dayB} ${monB}${year}`;
}

/**
 * X88: the days the strip's "Jump to a date" may pick, yyyy-MM-dd or null for no bound: a member from today to the
 * last day of the last week they can page to (as weekNav / shownDate); an admin (pastWeeks) any day at all.
 */
export function jumpWindow(todayDate, daysAhead, {pastWeeks = false} = {}) {
  if (pastWeeks) return {min: null, max: null};
  const {last} = weekBounds(todayDate, daysAhead);
  return {min: todayDate ?? null, max: last ? addDays(last, 6) : null};
}

/** "Mon 19" for a day button; the long form for a tooltip. */
export function dayHeader(date, todayDate) {
  const d = parse(date);
  if (!d) return {top: '', num: '', long: '', today: false};
  const dow = (d.getUTCDay() + 6) % 7;
  return {
    top: WEEKDAYS[dow], num: String(d.getUTCDate()), today: date === todayDate,
    long: d.toLocaleDateString('en-GB', {weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC'}),
  };
}

/**
 * The weeks the viewer may page through: from this week to the week holding
 * the last day they can book (Book up to N days), plus one more week so the
 * first days beyond the window show "Opens …" (as the old day strip did).
 */
export function weekBounds(todayDate, daysAhead) {
  const first = weekStart(todayDate);
  if (!first) return {first: null, last: null};
  const lastBookable = addDays(todayDate, Math.max(0, daysAhead ?? 7));
  const last = addDays(weekStart(lastBookable), 7);
  return {first, last};
}

/**
 * The previous / next arrows: the Monday to go to, or null with the reason
 * the arrow is off ("Past weeks aren't shown", "You can book up to 7 days ahead").
 */
export function weekNav(monday, todayDate, daysAhead, {pastWeeks = false, noLimitAhead = false} = {}) {
  const {first, last} = weekBounds(todayDate, daysAhead);
  const here = weekStart(monday);
  // X88: the admin calendar pages back without a lower limit (pastWeeks) and on without an upper one (noLimitAhead).
  const prev = here && (pastWeeks || (first && here > first)) ? addDays(here, -7) : null;
  const next = here && (noLimitAhead || (last && here < last)) ? addDays(here, 7) : null;
  const ahead = Math.max(0, daysAhead ?? 7);
  return {
    prev, next,
    prevHint: prev ? 'Previous week' : "Past weeks aren't shown",
    nextHint: next ? 'Next week' : `You can book up to ${ahead} day${ahead === 1 ? '' : 's'} ahead`,
    isCurrent: here === first,
  };
}

/**
 * The day the address asks for, kept inside the pageable weeks; else today
 * with a notice saying why (the old page's dateNotice, for weeks).
 *   {date, monday, notice}
 */
export function shownDate(wantedDate, todayDate, daysAhead) {
  const {first, last} = weekBounds(todayDate, daysAhead);
  if (!wantedDate) return {date: todayDate, monday: first, notice: null};
  if (!isDateKey(wantedDate)) return {date: todayDate, monday: first, notice: "That date couldn't be read, so today is shown."};
  const monday = weekStart(wantedDate);
  if (monday < first) return {date: todayDate, monday: first, notice: `${longDay(wantedDate)} has passed, so today is shown.`};
  if (monday > last) return {date: todayDate, monday: first, notice: `${longDay(wantedDate)} is too far ahead to show yet, so today is shown.`};
  return {date: wantedDate, monday, notice: null};
}

const longDay = (date) => parse(date).toLocaleDateString('en-GB', {weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC'});

/** "Opens Fri 24 Oct" from an ISO instant. */
export function opensWord(iso) {
  if (!iso) return 'Beyond your booking window';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Beyond your booking window';
  return `Opens ${d.toLocaleDateString('en-GB', {weekday: 'short', day: 'numeric', month: 'short'})}`;
}

// ── Groups (one grid per group: one slot step) ──
/**
 * The tabs: every group with at least one area shown (for the sport asked
 * for), in the API's order. `noun` is the X42 word for its areas when they
 * share one sport noun, else the club-wide one.
 *   [{id, name, slotMinutes, cancelCutoffMinutes, areas, bookableAreas, noun, nounPlural}]
 */
export function groupTabs(availability, sportId = null, {all = false} = {}) {
  if (!availability) return [];
  const areas = areasForSport(availability.areas, sportId);
  const sports = availability.sports ?? [];
  const tabs = (availability.groups ?? []).map((g) => {
    const mine = areas.filter((a) => a.groupId === g.id);
    if (!mine.length) return null;
    // Grant 2026-11-02: a tab of courts and boards is "court or board" / "courts and boards" (areaWords), never "playing area".
    const words = areaWords(availability, {areas: mine});
    return {
      id: g.id, name: g.name, slotMinutes: g.slotMinutes, cancelCutoffMinutes: g.cancelCutoffMinutes, areas: mine, bookableAreas: mine.filter((a) => a.canBeBooked), noun: words.noun, nounPlural: words.plural,
      contents: contentsLabel(mine, sports, availability.areaNounPlural ?? 'playing areas'),
    };
  }).filter(Boolean);
  const labels = tabLabels(tabs, sports);
  const named = tabs.map((t, i) => ({...t, label: labels[i]}));
  // Grant 2026-11-03 ("display all playing areas, with different booking rules, in one table"): /book asks for the All
  // tab (`all`), first, when the club has more than one set of rules; the admin calendar keeps one set per grid.
  const every = all ? allTab(named, availability) : null;
  return every ? [every, ...named] : named;
}

/** The All tab's id in the address (?group=all is the default, so it is left out). */
export const ALL_TAB_ID = 'all';

/**
 * Grant 2026-11-03: the "All" tab: every playing area of every booking rule set shown, as the columns of ONE table
 * (mergedGrid), first in the tabs when there is more than one. It carries its member tabs (`tabs`), the smallest slot
 * step (the row height's), the one cancel cut-off when every set shares it (else null: no chip) and the words of all
 * its areas. Null with fewer than two tabs.
 */
export function allTab(tabs, availability) {
  if (!tabs || tabs.length < 2) return null;
  const areas = tabs.flatMap((t) => t.areas);
  const words = areaWords(availability, {areas});
  const cutoffs = new Set(tabs.map((t) => t.cancelCutoffMinutes));
  return {
    id: ALL_TAB_ID, all: true, tabs, name: 'All', label: 'All', contents: 'All', areas, bookableAreas: areas.filter((a) => a.canBeBooked),
    slotMinutes: Math.min(...tabs.map((t) => Math.max(1, Number(t.slotMinutes) || 60))), cancelCutoffMinutes: cutoffs.size === 1 ? [...cutoffs][0] : null,
    noun: words.noun, nounPlural: words.plural,
  };
}

/** The tabs a tab stands for: its members for the All tab, itself otherwise. */
export function tabsOf(tab) {
  return tab?.all ? tab.tabs ?? [] : tab ? [tab] : [];
}

/** The cell context for one member tab of the All tab (the page's `forTab`), else the context itself. */
const ctxOf = (ctx, sub) => (ctx?.forTab && sub && !sub.all ? ctx.forTab(sub) ?? ctx : ctx);

/** The words a playing area's own name can carry ("R4 Table", "X41 Board 1", "Centre Court"), when its sports don't say (Grant 2026-11-02: kept in utils/areaWords, re-exported here). */
export {NAME_NOUNS};

/**
 * The X42 noun of one area ("court"): its sports' shared word, else the word
 * in its own name (an area with no sport plays any: "R4 Table" is a table),
 * else null.
 */
export function areaNoun(area, sports) {
  const ids = area?.sportIds ?? [];
  const nouns = new Set(ids.map((id) => (sports ?? []).find((s) => s.id === id)?.venueNoun).filter(Boolean));
  if (nouns.size === 1) return [...nouns][0];
  if (nouns.size > 1) return null;
  const words = String(area?.name ?? '').toLowerCase().split(/[^a-z]+/).filter(Boolean);
  const found = words.filter((w) => NAME_NOUNS.includes(w) || NAME_NOUNS.includes(w.replace(/s$/, '')));
  return found.length ? found[found.length - 1].replace(/s$/, '') : null;
}

const cap = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : '');

/**
 * X54 (ruling 2): a tab is named by what it holds: "Courts", "Boards",
 * "Tables & courts" (the nouns in the order the areas come, the last joined
 * with "&"), never "All playing areas" or "Booking rules". An area whose sports disagree, or
 * that has none, counts as the club-wide word ("playing areas").
 */
export function contentsLabel(areas, sports, fallbackPlural = 'playing areas') {
  const plurals = (areas ?? []).map((a) => {
    const one = areaNoun(a, sports);
    return one ? ((sports ?? []).find((s) => s.venueNoun === one)?.venueNounPlural || one + 's') : fallbackPlural;
  });
  return contentsFromNouns(plurals, fallbackPlural);
}

/**
 * X57: the join rule on its own, from the plural word each area counts as
 * (the API's `nounPlural` on a group's areas, or contentsLabel's nouns):
 * "Courts", "Tables & courts", "Courts, tables & boards"; nothing → the
 * fallback, capitalised. The same rule names a group on the API
 * (PlayingAreaNouns.ContentsName), so what the dialog offers is what is saved.
 * X60 (R24 L-b): the fallback word ("playing areas") is not a noun, so it is
 * never listed beside real ones ("Courts, boards & playing areas"): as soon as
 * ONE area has no noun (null, '', or the fallback itself) the whole set is the
 * fallback, "Playing areas", the honest word for a mixed set.
 */
export function contentsFromNouns(plurals, fallbackPlural = 'playing areas') {
  const fallback = String(fallbackPlural || 'playing areas').trim().toLowerCase();
  const nouns = [];
  for (const p of plurals ?? []) {
    const word = String(p || '').trim().toLowerCase();
    if (!word || word === fallback || word === 'playing areas') return cap(fallback);
    if (!nouns.includes(word)) nouns.push(word);
  }
  if (!nouns.length) return cap(fallback);
  return cap(nouns.length === 1 ? nouns[0] : nouns.slice(0, -1).join(', ') + ' & ' + nouns[nouns.length - 1]);
}

/**
 * The placeholder name a club's first, empty set of booking rules gets from the API: "Booking rules" (2026-11-04), and
 * "All playing areas" before that. A group still called either is offered a better one (ruling 2, admin side) and its
 * tab is labelled by its contents, never treated as admin-named.
 */
export const DEFAULT_GROUP_NAME = 'All playing areas';
export const DEFAULT_GROUP_NAMES = Object.freeze(['Booking rules', DEFAULT_GROUP_NAME]);

export function isDefaultGroupName(name) {
  const n = String(name ?? '').trim().toLowerCase();
  return DEFAULT_GROUP_NAMES.some((d) => d.toLowerCase() === n);
}

/**
 * X57 (ruling 2): what a group still called "All playing areas" (or "Booking rules") would be
 * called once the areas in `movingAreaIds` have left it for a new group, from
 * the API's `nounPlural` on each of its areas; null when it has a real name,
 * keeps no areas, or already reads that. The New group dialog shows it as
 * "Rename 'All playing areas' to 'Tables & courts'?", ticked by default.
 */
export function renameSuggestion(group, movingAreaIds = []) {
  if (!group || !isDefaultGroupName(group.name)) return null;
  const moving = new Set(movingAreaIds ?? []);
  const kept = (group.playingAreas ?? []).filter((a) => !moving.has(a.id));
  if (!kept.length) return null;
  const name = contentsFromNouns(kept.map((a) => a.nounPlural));
  return name === group.name ? null : name;
}

/** "court" → "courts", "pitch" → "pitches". */
const pluralOf = (n) => (/(ch|sh|s|x|z)$/.test(n) ? n + 'es' : n + 's');

/**
 * X74 (ruling 2, R25 L2): whether the admin gave the group a real name of their own ("Tennis court bookings", "Main
 * hall", "Indoor"), shown on its tab as is. A default or generated name ("All playing areas", "Booking rules", "Playing areas", "Courts",
 * "Tables & courts", a sport's own venue plural) is not: that tab is named by what it holds.
 */
export function ownGroupName(name, tab = null, sports = []) {
  const n = String(name ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
  if (!n || isDefaultGroupName(n)) return false;
  if (tab?.contents && n === String(tab.contents).trim().toLowerCase()) return false;
  const plurals = new Set(['playing areas', ...NAME_NOUNS.map(pluralOf), ...(sports ?? []).map((s) => String(s?.venueNounPlural ?? '').trim().toLowerCase()).filter(Boolean)]);
  const tokens = n.split(/\s*(?:,|&|\band\b)\s*/).filter(Boolean);
  return !tokens.every((t) => plurals.has(t));
}

/**
 * The tab labels: the group's own name when the admin gave it one (X74), else
 * what it holds; a clash told apart: two groups of courts on different slot
 * lengths read "Courts · 30 min" / "Courts · 1 h"; on the same slot length
 * the group's own name is the only thing left to tell them apart.
 */
export function tabLabels(tabs, sports = []) {
  const base = (tabs ?? []).map((t) => (ownGroupName(t.name, t, sports) ? String(t.name).trim() : t.contents ?? t.name));
  return (tabs ?? []).map((t, i) => {
    const same = tabs.filter((_, j) => base[j] === base[i]);
    if (same.length === 1) return base[i];
    const sameLength = same.filter((o) => o.slotMinutes === t.slotMinutes);
    if (sameLength.length === 1) return `${base[i]} · ${durationShort(t.slotMinutes)}`;
    return t.name;
  });
}

/**
 * "Book a court" / "Book a table or court" / "Book a court, table or board" from the nouns of the areas shown (D2 item 8).
 * Grant 2026-11-02: every noun is named, an area that names nothing is left out, the API's bookLabel stands in when none
 * does, and "Book a playing area" only when nothing names anything (areaWords; "Book an oche" takes its article).
 */
export function bookTitle(availability, sportId = null) {
  return areaWords(availability, {areas: areasForSport(availability?.areas, sportId)}).bookLabel;
}

/** The tab to show: the one asked for when it exists, else the first. */
export function pickTab(tabs, groupId) {
  return tabs.find((t) => t.id === groupId) ?? tabs[0] ?? null;
}

/** "CSW Court 2 and CSApp Court A are open play: no booking needed." */
export function walkUpLine(tab) {
  const names = (tab?.areas ?? []).filter((a) => !a.canBeBooked).map((a) => a.name);
  if (!names.length) return '';
  const list = names.length === 1 ? names[0] : names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1];
  return `${list} ${names.length === 1 ? 'is' : 'are'} open play: no booking needed.`;
}

// ── Rows (one step, labelled on the hour) ──
/** Minutes from midnight of a slot label ("18:30" → 1110, "01:00 (+00:00)" → 60). */
export function minutesOf(label) {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(label ?? '').trim());
  return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : null;
}

/**
 * A day's slots for a group, keyed by their row minute (a slot past midnight
 * keeps counting: 01:00 after 23:30 is row 1500, not 60).
 */
export function slotsByRow(day, groupId) {
  const slots = [...(day?.groups?.find((g) => g.groupId === groupId)?.slots ?? [])].sort((a, b) => a.startsAt.localeCompare(b.startsAt));
  const out = new Map();
  let last = -1;
  let wrap = 0;
  for (const s of slots) {
    const m = minutesOf(s.label);
    if (m == null) continue;
    if (m + wrap < last) wrap += 1440;
    const row = m + wrap;
    last = row;
    if (!out.has(row)) out.set(row, s);
  }
  return out;
}

/**
 * The rows for one group over several days: every slot start, on the group's
 * step. A row starting on the hour is marked `hour` (QA 2026-11-04: ONLY on
 * the hour, never the first row of each clock hour, which put the strong
 * line at 8:20 on 40-minute slots); the rest are guide lines.
 *   [{minutes, label, hour}]
 */
export function weekRows(days, groupId) {
  const starts = new Set();
  for (const day of days ?? []) for (const m of slotsByRow(day, groupId).keys()) starts.add(m);
  const rows = [...starts].sort((a, b) => a - b).map((minutes) => ({minutes, label: clock24(minutes), hour: false}));
  for (const r of rows) r.hour = r.minutes % 60 === 0;
  return rows;
}

/**
 * The rows of ONE day for a group. X95 (R36 H1): built by the shared slotRows (utils/bookingRows.js), the admin
 * calendar's own builder: one row per slot (a slot off the step, 14:30 on a 40-minute set, starts its own row and is
 * never cut into 14:30–14:40 + 14:40–15:10), a gap between slots filled on the step from the slot before it and cut at
 * the next slot (the open play there). Each row carries its own end (`end`, minutes). Labelled on the hour like weekRows.
 *   [{minutes, end, label, hour}]
 */
export function dayRows(day, groupId, step) {
  const byRow = slotsByRow(day, groupId);
  if (!byRow.size) return [];
  const slots = [...byRow].map(([m, slot]) => ({start: m, end: m + Math.max(1, Math.round((new Date(slot.endsAt) - new Date(slot.startsAt)) / 60000))}));
  const rows = slotRows(slots, Math.max(1, Number(step) || 60)).map((r) => ({minutes: r.start, end: r.end, label: clock24(r.start), hour: false}));
  for (const r of rows) r.hour = r.minutes % 60 === 0;
  return rows;
}

/**
 * X91: each row's whole period for the time column ("9:40–10:20" on a 40-minute step): from its start to its end (X95:
 * the row's own `end`, so a short gap row reads "14:00–14:30"), else the next row's start, else its start plus the step.
 *   [{minutes, label, hour, period}]
 */
export function rowPeriods(rows, step) {
  const s = Math.max(1, Number(step) || 60);
  return (rows ?? []).map((r, i) => ({...r, period: periodWord(r.minutes, r.end ?? rows[i + 1]?.minutes ?? r.minutes + s)}));
}

/** X95: where row `i` ends, in minutes (its own end, else the next row's start, else its start plus the step). */
export function rowEnd(rows, i, step = 60) {
  const r = rows?.[i];
  if (!r) return null;
  return r.end ?? rows[i + 1]?.minutes ?? r.minutes + (Number(step) || 60);
}

/** X95: the minutes rows `row` … `row + span - 1` cover (their real lengths, a short gap row included). */
export function rowsMinutes(rows, row, span, step = 60) {
  const end = rowEnd(rows, Math.min((rows?.length ?? 0) - 1, row + span - 1), step);
  const start = rows?.[row]?.minutes;
  return start == null || end == null ? span * (Number(step) || 60) : end - start;
}


/** A row's height in px for a slot step: 60 → 52, 40 → 40, 30 → 34, 15 → 28, 120 → 60. */
export function rowHeight(step) {
  const s = Number(step) || 60;
  return Math.round(Math.min(60, Math.max(28, 16 + s * 0.6)));
}

// ── Cells ──
/**
 * Every state a cell can be in, and the legend class that draws it. The
 * legend shows one swatch per class: the grid and the legend render the same
 * component with the same class, so they can't drift (Grant 2026-10-19).
 */
export const CELL_STATES = Object.freeze({
  free: {cls: 'free'},
  taken: {cls: 'taken'},
  mine: {cls: 'mine'},
  open: {cls: 'open', icon: 'mdi-walk'},
  // X91 (Grant 2026-10-21: "Items in the past shouldn't be labelled 'Past' they should just have a lower alpha"): a slot
  // that has started is a plain cell with no word, not bookable; once it has ended it is faded (`past`, BookingCell's one
  // .bwc--past), as a booking that has ended is. The slot under way now is not faded.
  // QA 2026-11-04 (round 3): an ended free slot is its own look (the kit's light grey, a muted "Ended"), never a white
  // cell that reads as free, nor the taken grey or a hatched band.
  past: {cls: 'ended'},
  closed: {cls: 'cant', icon: 'mdi-cancel'},
  frozen: {cls: 'cant', icon: 'mdi-snowflake'},
  matchesOnly: {cls: 'cant', icon: 'mdi-whistle'},
  notYetOpen: {cls: 'cant', icon: 'mdi-lock-clock'},
  limit: {cls: 'cant', icon: 'mdi-lock-outline'},
  noAccess: {cls: 'cant', icon: 'mdi-lock-outline'},
  /** X54 (D2 H1): a priced slot while the club can't take a card payment. */
  noPayments: {cls: 'cant', icon: 'mdi-credit-card-off-outline'},
});

/**
 * The viewer's allowance on a group: the per-group line when the API gives
 * it, else the viewer's own (admins and single-group clubs).
 */
export function groupAllowance(viewer, groupId) {
  return viewer?.byGroup?.[groupId] ?? viewer ?? null;
}

/**
 * X74 (R25 M1): the bookings that count against "at once" on a tab: the group's own count when the API gives it (it is
 * a per-group rule, counted exactly as the quote counts it), else the viewer's club-wide one (older APIs).
 */
export function activeOn(viewer, allowance = null) {
  return allowance?.activeBookings ?? viewer?.activeBookings ?? 0;
}

/**
 * The viewer has every booking they're allowed coming up, on this tab.
 * Grant 2026-11-02 ("I am an admin but it is now saying 'You have 2 bookings coming up, the most you can have at once'";
 * his ruling, "Admin can do what they want with a warning"): never a club admin. The API sends them no limit at all
 * (BookingService.AdminDaysAhead's note); this keeps an older answer from saying it either. The quote's chips are the warning.
 */
export function atLimit(viewer, allowance = null) {
  if (viewer?.isAdmin) return false;
  const max = allowance?.maxActiveBookings ?? viewer?.maxActiveBookings;
  return max != null && activeOn(viewer, allowance) >= max;
}

/**
 * What one grid cell is, for one slot of a day on one playing area:
 *   {state, cls, icon, text, mini, tip, bookable, past, areaId, startsAt, date}
 * `text` is the cell's word at full width, `mini` on a phone, `tip` the
 * sentence shown on hover or tap. `bookable` is true ONLY when the API's
 * slot is bookable, the area is free, and this viewer may book here now.
 *
 * ctx: {viewer, allowance, canTap, tapReason, priceMinor, currency, now (ISO), noun, slotMinutes, opensAt, date, paymentsRefusal}
 */
export function cellModel(slot, area, ctx) {
  const c = ctx ?? {};
  const noun = c.noun ?? 'playing area';
  const base = {startsAt: slot?.startsAt ?? null, endsAt: slot?.endsAt ?? null, date: c.date ?? null, areaId: area?.id ?? null, bookable: false, past: false, text: '', mini: '', bookingKey: null};
  const done = (state, extra) => ({...base, state, cls: CELL_STATES[state].cls, icon: CELL_STATES[state].icon ?? null, ...extra});
  // X91: every time in the grid is the 24-hour grid clock (clockWord / clock24, the time column's).
  const when = slot ? clockWord(slot.label) : '';

  // Outside the group's hours that day: open play, nothing to book.
  if (!slot) return done('open', {text: 'Open play', tip: 'Open play: no booking needed, just turn up.'});

  const cell = (slot.areas ?? []).find((x) => x.areaId === area?.id) ?? null;
  // Grant 2026-11-03 ("Even if a slot has started, I still think you should be able to book it"): a slot under way
  // (started, not ended) is as bookable as one still to come, at its own start, end and price; it is marked "Now".
  // Only a slot that has ended is a faded past cell.
  const started = !!c.now && slot.startsAt <= c.now;
  const ended = !!c.now && !!slot.endsAt && slot.endsAt <= c.now;
  const span = `${when}–${endWord(slot)}`;
  const name = area?.name ?? noun;

  // One booking spans several cells: the same key on each lets columnBlocks draw it as one block (ruling 6).
  const bookingKey = cell ? (cell.bookingId ?? `${cell.kind ?? ''}|${cell.label ?? ''}`) : null;
  const realBooking = !!cell?.bookingId;
  if (cell && (cell.mine || cell.status === 'mine')) {
    return done('mine', {text: 'You', mini: 'You', past: ended, bookingKey, realBooking, tipLead: `Yours: ${name}`, tip: `Yours: ${name}, ${span}.`});
  }
  if (cell && ['booked', 'match'].includes(cell.status)) {
    const word = cellWord(cell) || 'Taken';
    // X233 (R97 O-2): a club block (the API's label "Club", kind Admin) is "The club", as everywhere else (BookingWording.Who).
    const club = cell.kind === 'Admin' || cell.label === 'Club';
    const who = cell.status === 'match' ? (cell.label === 'Meet' ? 'a meet is on' : 'a match is on') : club ? 'The club' : `booked by ${cell.label && cell.label !== 'Booked' ? cell.label : 'someone'}`;
    return done('taken', {text: word, mini: word.length <= 5 ? word : '', past: ended, bookingKey, realBooking, tipLead: `Taken: ${who}`, tip: `Taken: ${who}, ${span}.`});
  }
  if (ended) return done('past', {past: true, text: 'Ended', mini: 'Ended', tip: 'Already gone.'});
  if (cell?.status === 'free' && slot.bookable) {
    const refused = !!c.allowance && c.allowance.canBook === false;
    // X58 (R23a M1): the club can't take a card payment: its own state with the card-off icon, checked before the general
    // no-access path so the legend's card-off swatch is what every such cell shows. The API says it on the group
    // (ByGroup.Reason, which turns its CanBook off), on the viewer (Reason, which turns canTap off for a member) or only as
    // PaymentsRefusal beside a price (an admin keeps CanBook for the calendar); at their limit the limit wins (it is about them).
    const saidWhy = refused ? c.allowance.reason : !c.canTap ? (c.viewer?.reason ?? c.tapReason) : null;
    const paymentsOff = !!c.paymentsRefusal && (saidWhy != null ? saidWhy === c.paymentsRefusal : !!c.priceMinor && !atLimit(c.viewer, c.allowance));
    if (paymentsOff) return done('noPayments', {text: "Can't book", tip: c.paymentsRefusal});
    if (c.canTap && !refused) {
      if (atLimit(c.viewer, c.allowance)) {
        const max = c.allowance?.maxActiveBookings ?? c.viewer?.maxActiveBookings;
        return done('limit', {text: 'At limit', tip: `You already have ${max} booking${max === 1 ? '' : 's'} coming up, the most you can have at once. Cancel one to book another.`});
      }
      const price = c.priceMinor ? moneyExact(c.priceMinor, c.currency) : null;
      const forWords = price ? `${price} for ${durationShort(c.slotMinutes)}` : 'free of charge';
      // QA 2026-11-04: a plain "Free" (no price) is drawn quieter than a price or a booking (`gratis`), so a table of
      // free cells doesn't shout; the white cell itself says bookable.
      if (started) return done('free', {text: price ?? 'Free', mini: 'Now', now: true, gratis: !price, bookable: true, tip: `Available now: ${name}, ${span} (started ${when}), ${forWords} for the whole slot. Tap to book.`});
      return done('free', {text: price ?? 'Free', mini: price ?? 'Free', gratis: !price, bookable: true, tip: `Available: ${name}, ${span}, ${forWords}. Tap to book.`});
    }
    // The group's own reason first (X54/X60: "This club can't take card payments online right now. Ask the club how to pay." on a priced tab), else the viewer's.
    return done('noAccess', {tip: (c.allowance?.canBook === false && c.allowance.reason) || c.tapReason || "You can't book here."});
  }
  if (cell?.status === 'notYetOpen') return done('notYetOpen', {text: 'Not yet', tip: `${opensWord(c.opensAt)}: that's as far ahead as you can book.`});
  if (!cell || cell.status === 'open') return done('open', {text: 'Open play', tip: 'Open play: no booking needed, just turn up.'});
  // X95 (R36 L6): a freeze with no reason comes labelled "Closed" (the hours' word); it is just "Frozen".
  if (cell.status === 'frozen') { const why = cell.label && cell.label !== 'Closed' ? cell.label : null; return done('frozen', {text: 'Frozen', tip: `Frozen${why ? ': ' + why : ''}. The club has closed this ${noun} for a while.`}); }
  if (cell.status === 'matchesOnly') return done('matchesOnly', {text: 'Matches only', tip: 'Matches only: this time is kept for league and ladder matches.'});
  if (cell.status === 'closed') return done('closed', {text: 'Closed', tip: `Closed: the club doesn't open this ${noun} then.`});
  return done('noAccess', {tip: "This time can't be booked right now."});
}

const endWord = (slot) => {
  const m = minutesOf(slot?.label);
  if (m == null) return '';
  const mins = Math.round((new Date(slot.endsAt) - new Date(slot.startsAt)) / 60000);
  return clock24(m + mins, {end: true});
};

/** "30 min" / "1 h" / "1 h 30" for the price words. */
export function durationShort(minutes) {
  const m = Number(minutes) || 0;
  if (m < 60) return `${m} min`;
  if (m % 60 === 0) return `${m / 60} h`;
  return `${Math.floor(m / 60)} h ${m % 60}`;
}

/**
 * One day of one tab: the day's rows (its own span on the group's step) and a
 * column per playing area of the group (the club's own names), each a cell
 * model per row and, X54, the blocks those cells make (one per booking, one
 * band per run of a state) plus how many leading rows have already started
 * (the "Earlier today" band).
 *   {rows, step, columns: [{area, cells: [model], blocks: [block]}], empty, note, beyond, opensAt, startedRows}
 */
export function dayGrid(days, date, tab, ctx = {}) {
  if (tab?.all) return mergedGrid(days, date, tab, ctx);
  const dayList = days ?? [];
  const day = dayList.find((d) => d.date === date) ?? null;
  const rows = tab && day ? dayRows(day, tab.id, tab.slotMinutes) : [];
  const byRow = tab && day ? slotsByRow(day, tab.id) : new Map();
  const columns = (tab?.areas ?? []).map((area) => {
    const cells = rows.map((r) => cellModel(byRow.get(r.minutes) ?? null, area, {...ctx, date, slotMinutes: tab?.slotMinutes, opensAt: day?.opensAt ?? null}));
    return {area, cells, blocks: columnBlocks(cells, rows, tab?.slotMinutes)};
  });
  const closed = !!tab && closedAllDay(day, tab.id);
  return {
    rows: closed ? [] : rows, step: tab?.slotMinutes ?? 60, columns: closed ? [] : columns, empty: closed || rows.length === 0 || columns.length === 0 || !day, closedAllDay: closed,
    // Grant 2026-11-02 ("it flashes the open-play words instead of a table skeleton loader"): the day's data hasn't
    // arrived (the week is still loading, or its load failed) is NOT the same as a loaded day with nothing on it; the
    // grid draws its skeleton for a pending day and says Open play / Closed only once the day is here.
    pending: !day,
    note: day?.groups?.find((g) => g.groupId === tab?.id)?.note ?? day?.note ?? '', beyond: !!day?.beyondWindow, opensAt: day?.opensAt ?? null,
    startedRows: startedRows(rows, byRow, ctx.now),
    // QA 2026-11-04: the band's count is of real slots folded (a gap row is not a slot).
    startedSlots: rows.slice(0, startedRows(rows, byRow, ctx.now)).filter((r) => byRow.has(r.minutes)).length,
    // Grant 2026-11-04 ("a subtle red line indicating the current time"): now on the club's clock, in minutes from the
    // day's midnight, on today only (ctx.todayDate, the club's date); the grid draws the line where it falls.
    nowMin: ctx.todayDate && ctx.todayDate === date ? clubMinutesNow(day, ctx.now) : null,
  };
}

// ── Grant 2026-11-03: the All tab, every rule set in one table ──
/**
 * Grant 2026-11-04 ("the time row now tries to mix up both time scales when they are different, it should be
 * consistent and the book cells just overlap"): the All table's time column is ONE regular scale for the day, never
 * the merged boundaries of the sets: half-hour rows when any set shown runs under an hour, hour rows otherwise. The
 * cells sit over it by their own minutes (a 40-minute slot from 6:20 just crosses the lines).
 */
export function scaleStepFor(steps) {
  return (steps ?? []).some((s) => (Number(s) || 60) < 60) ? 30 : 60;
}

/**
 * The scale rows down the left of the All table: regular rows of `step` minutes from the scale line at or before the
 * earliest slot start to the one at or after the latest slot end. A set closed all day (no spans) adds nothing.
 *   sets: [[{start, end}]] (minutes) → [{start, end}]
 */
export function scaleRows(sets, step = 60) {
  const s = Math.max(1, Number(step) || 60);
  let first = Infinity;
  let last = -Infinity;
  for (const set of sets ?? []) for (const x of set ?? []) {
    if (!Number.isFinite(x?.start) || !Number.isFinite(x?.end) || x.end <= x.start) continue;
    first = Math.min(first, x.start);
    last = Math.max(last, x.end);
  }
  if (!Number.isFinite(first)) return [];
  const from = Math.floor(first / s) * s;
  const to = Math.ceil(last / s) * s;
  const rows = [];
  for (let t = from; t < to; t += s) rows.push({start: t, end: t + s});
  return rows;
}

/** Rows with their labels, a row starting on the hour marked `hour` (its line is stronger), as weekRows / dayRows. */
function labelRows(rows) {
  const out = rows.map((r) => ({minutes: r.start, end: r.end, label: clock24(r.start), hour: false}));
  for (const r of out) r.hour = r.minutes % 60 === 0;
  return out;
}

/** A group's real (not made-up) slots as minute spans, for scaleRows. */
export function slotSpans(day, groupId) {
  return [...slotsByRow(day, groupId)].map(([start, slot]) => ({start, end: start + Math.max(1, Math.round((new Date(slot.endsAt) - new Date(slot.startsAt)) / 60000))}));
}

/**
 * The rows of one column of the All table: the group's own rows (dayRows: its slots, gaps on its step), stretched to
 * the table's whole span with open-play rows on its step before its first slot and after its last, so every column
 * tiles the same axis and columnBlocks can band them.
 */
export function dayRowsWithin(day, groupId, step, axisStart, axisEnd) {
  const spans = slotSpans(day, groupId);
  const s = Math.max(1, Number(step) || 60);
  const first = spans.length ? Math.min(...spans.map((x) => x.start)) : axisEnd;
  const last = spans.length ? Math.max(...spans.map((x) => x.end)) : axisStart;
  if (axisStart < first) spans.push({start: axisStart, end: first});
  if (axisEnd > last) spans.push({start: Math.max(last, axisStart), end: axisEnd});
  return labelRows(slotRows(spans, s));
}

/** Now on the club's clock, in minutes from the day's midnight, read from any slot of the day (null without one). */
export function clubMinutesNow(day, now) {
  const t = nowMs(now);
  if (t == null) return null;
  for (const g of day?.groups ?? []) for (const s of g.slots ?? []) {
    const m = minutesOf(s.label);
    const at = new Date(s.startsAt).getTime();
    if (m != null && Number.isFinite(at)) return m + (t - at) / 60000;
  }
  return null;
}

/**
 * The px per minute of the All table: the smallest slot step keeps its own row height (rowHeight), snapped to a fifth
 * of a pixel so every five-minute boundary lands on a whole pixel (one crisp hairline, never two blurred ones):
 * 60 → 0.8 (48px an hour), 40 → 1, 30 → 1.2, 15 → 1.8.
 */
export function minutePx(step) {
  const s = Math.max(1, Number(step) || 60);
  return Math.round((rowHeight(s) / s) * 5) / 5;
}

/** The All table's px per minute on a phone: the smallest slot never under 40px, snapped the same way (ceiling). */
export function phoneMinutePx(step) {
  const s = Math.max(1, Number(step) || 60);
  return Math.max(minutePx(s), Math.ceil((40 / s) * 5) / 5);
}

/**
 * Grant 2026-11-03: one day of the All tab: every playing area of every rule set as the columns of one table. Each
 * column keeps its own set's slots, cells and blocks (its own step, prices, allowance and noun: a tap books with that
 * area's own rules), carried as minutes from the day's midnight (`startMin` / `endMin` on every cell and block) and drawn
 * by them, proportional to their length, so a 40-minute slot and a 60-minute one side by side differ visibly and a
 * slot straddles the rows wherever its own start and end fall. The rows are one regular scale (scaleRows, half-hours
 * or hours), never the sets' own boundaries; the cells tile from the earliest start to the latest end (cellStart /
 * cellEnd) and the scale's margins are gaps. A set closed all day is one "Closed all day" block down its columns; a
 * set with no hours is open play down its columns.
 *   {rows, step, scaleStep, proportional: true, axisStart, axisEnd, cellStart, cellEnd, pxPerMinute, phonePxPerMinute,
 *    columns: [{area, tab, slotMinutes, allowance, priceMinor, noun, rows, cells, blocks}], empty, closedAllDay,
 *    pending, note, beyond, opensAt, startedRows, startedSlots}
 */
export function mergedGrid(days, date, tab, ctx = {}) {
  const dayList = days ?? [];
  const day = dayList.find((d) => d.date === date) ?? null;
  const subs = tabsOf(tab);
  const parts = subs.map((sub) => ({sub, closed: !!day && closedAllDay(day, sub.id), spans: day ? slotSpans(day, sub.id) : []}));
  const open = parts.filter((p) => !p.closed && p.spans.length);
  const scaleStep = scaleStepFor(open.map((p) => p.sub.slotMinutes));
  const rows = labelRows(scaleRows(open.map((p) => p.spans), scaleStep));
  const axisStart = rows[0]?.minutes ?? 0;
  const axisEnd = rows.length ? rows[rows.length - 1].end : 0;
  // The cells tile from the earliest slot start to the latest end; the scale's margins either side are gaps.
  const cellStart = open.length ? Math.min(...open.flatMap((p) => p.spans.map((x) => x.start))) : axisStart;
  const cellEnd = open.length ? Math.max(...open.flatMap((p) => p.spans.map((x) => x.end))) : axisEnd;
  const step = tab?.slotMinutes ?? 60;
  const columns = [];
  for (const p of parts) {
    const sub = p.sub;
    const sctx = ctxOf(ctx, sub);
    const base = {...sctx, date, slotMinutes: sub.slotMinutes, opensAt: day?.opensAt ?? null, noun: sub.noun ?? sctx?.noun};
    const byRow = day ? slotsByRow(day, sub.id) : new Map();
    const colRows = p.closed || !rows.length ? [{minutes: cellStart, end: cellEnd, label: clock24(cellStart), hour: true}] : dayRowsWithin(day, sub.id, sub.slotMinutes, cellStart, cellEnd);
    for (const area of sub.areas) {
      const cells = colRows.map((r) => {
        const model = p.closed
          ? cellModel({startsAt: null, endsAt: null, label: r.label, areas: [{areaId: area.id, status: 'closed'}]}, area, base)
          : cellModel(byRow.get(r.minutes) ?? null, area, base);
        return {...model, startMin: r.minutes, endMin: r.end};
      });
      const blocks = columnBlocks(cells, colRows, sub.slotMinutes);
      for (const b of blocks) {
        b.startMin = b.cells[0].startMin;
        b.endMin = b.cells[b.cells.length - 1].endMin;
        if (p.closed) Object.assign(b, {text: 'Closed all day', line: 'Closed all day', mini: 'Closed', tip: `Closed all day: the club doesn't open this ${sub.noun ?? 'playing area'} today.`});
      }
      columns.push({area, tab: sub, slotMinutes: sub.slotMinutes, allowance: sctx?.allowance ?? null, priceMinor: sctx?.priceMinor ?? null, noun: sub.noun, rows: colRows, cells, blocks});
    }
  }
  const closed = parts.length > 0 && parts.every((p) => p.closed);
  const nowMin = clubMinutesNow(day, ctx.now);
  const started = closed ? 0 : mergedStartedRows(rows, columns, nowMin);
  // QA 2026-11-04 ("6 slots" counted the half-hour scale rows): the band counts the sets' own slots that have ended
  // under the fold (a slot start of each set, whatever its areas), never the scale's rows.
  const foldMin = rows[started]?.minutes ?? axisEnd;
  const startedSlots = started ? open.reduce((n, p) => n + p.spans.filter((x) => x.end <= foldMin).length, 0) : 0;
  return {
    rows: closed ? [] : rows, step, scaleStep, proportional: true, axisStart, axisEnd, cellStart, cellEnd, pxPerMinute: minutePx(step), phonePxPerMinute: phoneMinutePx(step), columns: closed ? [] : columns,
    empty: closed || rows.length === 0 || columns.length === 0 || !day, closedAllDay: closed, pending: !day,
    note: parts.map((p) => day?.groups?.find((g) => g.groupId === p.sub.id)?.note ?? '').find((n) => restOpenPlayNote(n)) ?? day?.note ?? '',
    beyond: !!day?.beyondWindow, opensAt: day?.opensAt ?? null,
    startedRows: started, startedSlots,
    nowMin: ctx.todayDate && ctx.todayDate === date ? nowMin : null,
  };
}

/**
 * The All table's "Earlier today": the leading rows that have ended at `nowMin` with no cell under way across them (a
 * slot under way stays in the main table, bookable, however the rows fall; X94's rule by minutes).
 */
export function mergedStartedRows(rows, columns, nowMin) {
  if (nowMin == null || !rows?.length) return 0;
  const live = (r) => (columns ?? []).some((col) => (col.cells ?? []).some((c) => c.startMin < r.end && c.endMin > r.minutes && c.endMin > nowMin));
  let n = 0;
  for (const r of rows) {
    if (r.end > nowMin || live(r)) break;
    n++;
  }
  return n;
}

// ── X54: blocks and bands (rulings 6 and 7) ──
/**
 * How many leading rows have already started at `now`: the rows before the
 * first slot still to come (a gap row rides with its neighbours). The grid
 * folds them into one "Earlier today" band so the first bookable row sits
 * under the header (D2 item 5).
 */
export function startedRows(rows, byRow, now) {
  if (!now || !rows?.length) return 0;
  // X94 (R35 M3): only rows that have ENDED fold away; the slot under way stays outside the fold (a booking from 8:00
  // was drawn from 8:30 when the 8:00 slot it started in was folded).
  const i = rows.findIndex((r) => { const s = byRow?.get(r.minutes); return s && (s.endsAt ?? s.startsAt) > now; });
  return i < 0 ? rows.length : i;
}

/** Two neighbouring cells draw as one block: the same booking, or the same band state. Free cells never merge. */
export function joins(a, b) {
  if (!a || !b || a.state !== b.state) return false;
  if (a.state === 'free') return false;
  // X231: each too-short start is its own cell (its tooltip names its own time), as a free one is.
  if (a.state === 'short') return false;
  // X94 (R35 M4): an ended cell never joins one still under way, unless both are the same known booking (then the block
  // fades only once all of it has ended, as the admin calendar's does): two club bookings drawn as one "Club 8:00–10:30"
  // were faded whole when only the first had ended.
  if (!!a.past !== !!b.past && !(a.realBooking && b.realBooking && a.bookingKey === b.bookingKey)) return false;
  if (a.state === 'taken' || a.state === 'mine') return a.bookingKey != null && a.bookingKey === b.bookingKey && a.endsAt === b.startsAt;
  if (a.state === 'frozen') return a.tip === b.tip;
  return true;
}

/**
 * The blocks of one column: a booking is ONE block from its first slot to its
 * last (Taken, Yours, Match, Meet, Club alike), a run of Open play / Can't
 * book / Past is one band, a free cell stays a cell of its own (ruling 6).
 *   [{row, span, state, cls, icon, text, sub, line, mini, tip, bookable, past, cells, cell, areaId, startsAt, endsAt}]
 */
export function columnBlocks(cells, rows, slotMinutes = 60) {
  const blocks = [];
  (cells ?? []).forEach((c, i) => {
    const prev = blocks[blocks.length - 1];
    if (prev && prev.row + prev.span === i && joins(prev.cells[prev.cells.length - 1], c)) {
      prev.span++;
      prev.cells.push(c);
      prev.endsAt = c.endsAt ?? prev.endsAt;
      return;
    }
    blocks.push({row: i, span: 1, state: c.state, cls: c.cls, icon: c.icon ?? null, bookable: !!c.bookable, gratis: !!c.gratis, past: !!c.past, tip: c.tip ?? '', cells: [c], cell: c, areaId: c.areaId, startsAt: c.startsAt, endsAt: c.endsAt});
  });
  for (const b of blocks) {
    Object.assign(b, blockWords(b, rows ?? [], slotMinutes));
    // X94 (R35 M4): faded only once every slot of it has ended.
    b.past = b.cells.every((c) => !!c.past);
    // X94 (R35 L3): a booking's tooltip names its whole period ("Yours: Court 1, 20:00–22:00."), not its first slot.
    if (b.cell.tipLead && b.range) b.tip = `${b.cell.tipLead}, ${b.range}.`;
  }
  return blocks;
}

/** The words on a block: `text` (the main word), `sub` (a second line when it fits), `line` (the one-line form), `mini` (a phone). */
export function blockWords(b, rows, slotMinutes = 60) {
  const first = b.cells[0];
  const start = rows[b.row]?.label ?? '';
  const end = rows[b.row + b.span]?.label ?? (rows[b.row + b.span - 1] ? clock24(rowEnd(rows, b.row + b.span - 1, slotMinutes), {end: true}) : '');
  const range = start && end ? `${start}–${end}` : start;
  switch (b.state) {
    // Grant 2026-11-03: the slot under way reads "£6.00 · Now" (a phone: "Now"), so it is clear it has started.
    case 'free': return first.now ? {text: first.text, sub: 'Now', line: `${first.text} · Now`, mini: 'Now', range} : {text: first.text, sub: null, line: first.text, mini: first.mini || first.text, range};
    case 'taken':
    case 'mine': return {text: first.text, sub: range, line: `${first.text} · ${range}`, mini: first.text, range};
    case 'open': {
      const until = b.row + b.span < rows.length ? `until ${end}` : null;
      return {text: 'Open play', sub: until ?? 'just turn up', line: until ? `Open play ${until}` : 'Open play', mini: 'Open play', range};
    }
    case 'past': return {text: 'Ended', sub: null, line: 'Ended', mini: 'Ended', range};
    case 'short': return {text: 'Too short', sub: null, line: 'Too short', mini: 'Too short', range};
    case 'notYetOpen': return {text: 'Not yet', sub: first.tip?.split(':')[0] ?? null, line: 'Not yet', mini: 'Not yet', range};
    case 'noAccess':
    case 'noPayments': return {text: "Can't book", sub: null, line: "Can't book", mini: "Can't book", range};
    default: return {text: first.text || "Can't book", sub: null, line: first.text || "Can't book", mini: first.text || "Can't", range};
  }
}

/** The legend's order: one key per cell STATE (X58, R23a M1: a Can't book swatch is the icon of the cells on screen, so each reason is its own entry). */
export const LEGEND_ORDER = Object.freeze(['free', 'taken', 'mine', 'open', 'noPayments', 'limit', 'noAccess', 'notYetOpen', 'matchesOnly', 'frozen', 'closed']);

/** The legend keys on screen, in the legend's order (only the states shown are listed, D2 item 9). */
export function legendKeys(columns, {pastShown = true} = {}) {
  const keys = new Set();
  for (const col of columns ?? []) {
    for (const b of col.blocks ?? []) {
      // X91: no Past entry (time gone by is faded, not named); the folded earlier rows list nothing.
      if (b.state === 'past') continue;
      if (b.past && !pastShown) continue;
      keys.add(b.state);
    }
  }
  return LEGEND_ORDER.filter((k) => keys.has(k));
}

// ── X54: drag to book a range (ruling 5) ──
const RANGE_REASONS = {
  taken: 'Taken', mine: 'Yours', open: 'Open play then', past: 'Already started', closed: 'Closed then', frozen: 'Frozen then',
  matchesOnly: 'Matches only then', notYetOpen: 'Not open yet', limit: 'At your limit',
};

/** The rows a press-and-drag covers in a column: whole rows from the pressed one to the pointer's, inside the grid. */
export function dragRange(originRow, pointerRow, totalRows) {
  return createRange({originRow, pointerRow, step: 1, totalRows});
}

/**
 * X242 (TEST_R101 M-1): the row a press lands on, from the pointer's offset into the pressed block (px from its top).
 * /book and the admin calendar (drag) keep the press on the block's first row, as a drag grows from there. The
 * scheduling table (noDrag) reads the row actually under the pointer anywhere in a tall block (the match's own booking,
 * or its session's), from the block's first shown row (a block cut by the "Earlier today" band starts at `hidden`).
 */
export function pressRow(blk, offsetPx, rowPx, {noDrag = false, hidden = 0} = {}) {
  const off = rowPx > 0 ? offsetPx / rowPx : 0;
  if (!noDrag) return blk.row + Math.max(0, Math.min(0.99, off));
  const top = Math.max(blk.row, hidden);
  const end = blk.row + (blk.span ?? 1);
  return top + Math.max(0, Math.min(end - top - 0.01, off));
}

/**
 * X242: what a click (or tap) on a block does: {tap: cell} picks a start, {explain: cell} says why not. In the scheduling
 * table a click on a later row inside a tall block picks that row's start when it is bookable (the match, or its whole
 * session, fits from there); the first row, or a row that isn't, means the block itself, as before.
 * X243 (TEST_FT-R3 M-B, one rule with the app): the first row is the first one SHOWN (`hidden`: an "Earlier today" band
 * cuts the block), where the block's words sit, so a click on the words always keeps the start (on another court's
 * block: the same time on that court).
 */
export function tapTarget(col, blk, originRow = null, {noDrag = false, hidden = 0} = {}) {
  const row = originRow == null ? null : Math.floor(originRow);
  const later = row != null && row > Math.max(blk.row, hidden);
  const inside = noDrag && later && (blk.span ?? 1) > 1 ? col?.cells?.[row] : null;
  if (inside && inside !== blk.cell && inside.bookable) return {tap: inside};
  return blk.bookable ? {tap: blk.cell} : {explain: blk.cell};
}

/**
 * Whether the viewer may book `span` cells from `row` in one go: every cell
 * bookable by them, back to back, and no longer than their longest booking.
 * The reason names the first thing in the way ("Taken", "Matches only then",
 * "Longer than your longest booking (2 h)").
 */
export function rangeValidity(cells, row, span, {maxDurationMinutes = null, allDay = false, slotMinutes = 60, minutes = null} = {}) {
  for (let r = row; r < row + span; r++) {
    const c = cells?.[r];
    if (!c) return {ok: false, reason: 'Outside booking hours'};
    if (!c.bookable) return {ok: false, reason: RANGE_REASONS[c.state] ?? c.tip ?? "Can't book"};
    if (r > row && cells[r - 1].endsAt !== c.startsAt) return {ok: false, reason: 'Not back to back'};
  }
  if (!allDay && maxDurationMinutes != null && (minutes ?? span * (Number(slotMinutes) || 60)) > maxDurationMinutes) {
    return {ok: false, reason: `Longer than your longest booking (${durationWords(maxDurationMinutes)})`};
  }
  return {ok: true, reason: null};
}

/**
 * "Court 2 · 18:00–19:30 · 1 h 30 · £12.00" on the drag ghost (X91: the grid's 24-hour clock) (ruling 5; X58, R23a L2: the area's name leads when given,
 * and the grid draws it on a line of its own above the time · length · price).
 */
export function rangeLabel(rows, row, span, {slotMinutes = 60, priceMinor = null, currency = 'gbp', areaName = null} = {}) {
  const start = rows?.[row]?.label;
  if (!start) return '';
  const minutes = Number(slotMinutes) || 60;
  const end = rows[row + span]?.label ?? clock24(rowEnd(rows, Math.min(rows.length - 1, row + span - 1), minutes), {end: true});
  const price = priceMinor ? moneyExact(priceMinor * span, currency) : 'Free';
  const details = `${start}–${end} · ${durationWords(rowsMinutes(rows, row, span, minutes))} · ${price}`;
  return areaName ? `${areaName} · ${details}` : details;
}

/** The viewer's longest booking from their allowance, in slots (null = all day). */
export function maxRangeSlots(allowance, slotMinutes) {
  if (!allowance || allowance.allDay || allowance.maxDurationMinutes == null) return null;
  return Math.max(1, Math.floor(allowance.maxDurationMinutes / Math.max(1, Number(slotMinutes) || 60)));
}

// ── X54: the allowance as chips (D2 item 8) ──
/**
 * "2 at a time" · "Cancel up to 1 hour before" · "1 coming up": the compact
 * form of the allowance line under the strip; `limitLine` is the alert when
 * the viewer has every booking they may have.
 *   {chips: [{icon, text}], limitLine}
 */
export function allowanceChips(viewer, allowance = null, cutoffMinutes = null) {
  const a = allowance ?? viewer;
  // X74 (R25 M1): a tab that refuses the viewer (a membership needed there) shows its reason, never the limit of another tab.
  if (allowance && allowance.canBook === false) return {chips: [], limitLine: null};
  if (!viewer?.canBook && !(a && a.canBook)) return {chips: [], limitLine: null};
  const chips = [];
  // Grant 2026-11-02: an admin has no "at a time" and no limit line (atLimit's note); their "coming up" count still shows.
  const max = viewer?.isAdmin ? null : (a?.maxActiveBookings ?? viewer?.maxActiveBookings);
  const active = activeOn(viewer, allowance);
  if (max != null) chips.push({icon: 'mdi-calendar-multiple', text: `${max} at a time`});
  const cut = cutoffLine(cutoffMinutes);
  if (cut) chips.push({icon: 'mdi-undo-variant', text: cut});
  if (active > 0) chips.push({icon: 'mdi-calendar-check', text: `${active} coming up`});
  const limitLine = max != null && active >= max ? `You have ${max} booking${max === 1 ? '' : 's'} coming up, the most you can have at once. Cancel one to book another.` : null;
  return {chips, limitLine};
}

/** How many cells of a day this viewer could book on the tab (the strip's "12 available"). */
export function dayFreeCount(day, tab, ctx = {}) {
  if (!day || !tab) return 0;
  if (tab.all) return tabsOf(tab).reduce((n, t) => n + dayFreeCount(day, t, ctxOf(ctx, t)), 0);
  let n = 0;
  for (const slot of slotsByRow(day, tab.id).values()) {
    for (const area of tab.areas) if (cellModel(slot, area, {...ctx, date: day.date, slotMinutes: tab.slotMinutes, opensAt: day.opensAt ?? null}).bookable) n++;
  }
  return n;
}

/** X229: "now" as milliseconds from an ISO string, a Date or a number (null when none or unreadable). */
function nowMs(now) {
  if (now == null || now === '') return null;
  const t = typeof now === 'number' ? now : new Date(now).getTime();
  return Number.isFinite(t) ? t : null;
}

/** X229: the slot has not started yet at `now` (always true without a now). Instants, so the club's clock holds. */
export function slotAhead(slot, now = null) {
  const t = nowMs(now);
  if (t == null) return true;
  const start = new Date(slot?.startsAt).getTime();
  return !Number.isFinite(start) || start > t;
}

/** Grant 2026-11-03: the slot has not ENDED at `now` (a slot under way is still bookable; always true without a now). */
export function slotLive(slot, now = null) {
  const t = nowMs(now);
  if (t == null) return true;
  const end = new Date(slot?.endsAt ?? slot?.startsAt).getTime();
  return !Number.isFinite(end) || end > t;
}

/** F-FIX1 B4 / X229: the strip's word for today once every booking hour has started (no bar; tooltip and label only). */
export const DONE_FOR_TODAY = 'Done for today';

/**
 * How many cells of a day ANYONE could book on the tab (free, in a bookable slot), whoever is looking.
 * X229 (the app's F-FIX1 B4, FT-M1: today read "Fully booked" at 23:20 on a free court): with `now`, slots that have
 * already started never count.
 */
export function dayCapacity(day, tab, now = null) {
  if (!day || !tab) return 0;
  if (tab.all) return tabsOf(tab).reduce((n, t) => n + dayCapacity(day, t, now), 0);
  let n = 0;
  for (const slot of slotsByRow(day, tab.id).values()) {
    // Grant 2026-11-03: the slot under way still counts (it can be booked); only ended ones drop out.
    if (!slot.bookable || !slotLive(slot, now)) continue;
    for (const area of tab.areas) if ((slot.areas ?? []).some((x) => x.areaId === area.id && x.status === 'free')) n++;
  }
  return n;
}

/**
 * X195: every bookable cell of the day on the tab, booked or free (not closed): the whole the meter's share is of.
 * X229: with `now`, only the slots still to come (today's share is of what is left of today).
 */
export function dayTotal(day, tab, now = null) {
  if (!day || !tab) return 0;
  if (tab.all) return tabsOf(tab).reduce((n, t) => n + dayTotal(day, t, now), 0);
  let n = 0;
  for (const slot of slotsByRow(day, tab.id).values()) {
    if (!slot.bookable || !slotLive(slot, now)) continue;
    for (const area of tab.areas) if ((slot.areas ?? []).some((x) => x.areaId === area.id && x.status !== 'closed')) n++;
  }
  return n;
}

/**
 * X54 (D2 bug 8): the strip's word for a day with nothing this viewer can
 * book: "Full" only when the day really is full; else why THEY can't ("Sign
 * in to book", "At limit", "Can't book"); an open-play day says so.
 */
export function zeroWord(day, tab, ctx = {}) {
  // X229 (F-FIX1 B4 on the web): with ctx.now, started slots never count (see dayCapacity).
  const now = ctx.now ?? null;
  const capacity = dayCapacity(day, tab, now);
  // Grant 2026-11-03: the All tab with nothing anyone can book says one word for every set: the word they all say, else
  // Full when any set is full (its booking hours all taken), Done for today when any has finished, Matches only when
  // any keeps time for matches, else Open play (a Closed set beside an open-play one is an open-play day).
  if (tab?.all && capacity === 0) {
    const words = [...new Set(tabsOf(tab).map((t) => zeroWord(day, t, ctxOf(ctx, t))))];
    if (words.length === 1) return words[0];
    for (const w of ['Full', DONE_FOR_TODAY, 'Matches only']) if (words.includes(w)) return w;
    return 'Open play';
  }
  // X94 (R35 M2): a day closed from start to end says Closed, never Full.
  if (capacity === 0 && closedAllDay(day, tab?.id)) return 'Closed';
  // X194: a day whose only hours are Closed (6:00–14:00, say) has nothing to fill: the rest of it is open play, never Full.
  // X202 (R82 M2: weekly Closed 6:00–14:00 + a Matches only change 8:00–10:00 read "Fully booked"): Full needs booking
  // hours (Booking needed slots) that are all taken. A day without any has nothing to fill: no bar, and the word says
  // what the day is instead (Open play when part of it is unpainted, else Matches only).
  // X229: Full needs booking hours still to come, all taken; a day whose booking hours have all started has nothing left
  // to fill: "Done for today" (no bar, the words in the tooltip and label), never Full.
  if (capacity === 0) {
    const all = [...slotsByRow(day, tab?.id).values()].filter((x) => !x.openPlay);
    const real = all.filter((x) => slotLive(x, now));
    const fillable = (x) => (x.bookable || x.kind === 'BookingNeeded') && (x.areas ?? []).some((c) => c.status !== 'closed');
    if (nowMs(now) != null && !real.some(fillable) && all.some(fillable)) return DONE_FOR_TODAY;
    if (real.some(fillable)) return 'Full';
    return real.some((x) => x.kind === 'MatchesOnly') && !hasUnpaintedTime(day, tab?.id) ? 'Matches only' : 'Open play';
  }
  if (!ctx.canTap) return ctx.viewer?.signedIn ? "Can't book" : 'Sign in to book';
  if (ctx.allowance && ctx.allowance.canBook === false) return "Can't book";
  if (atLimit(ctx.viewer, ctx.allowance)) return 'At limit';
  if (ctx.priceMinor && ctx.paymentsRefusal) return "Can't book";
  return 'Full';
}

/**
 * X94 (R35 M2): the day is closed all day for these booking rules: its slots run from 0:00 to 24:00 and every one of them
 * is closed on every playing area (a "Closed all day" change, or closed weekly hours), so the grids show one "Closed all day" state instead
 * of 48 closed rows from 0:00 to 24:00, and the strip says Closed.
 */
export function closedAllDay(day, groupId) {
  // X194 (Grant 2026-10-27: Closed 6:00–14:00 on a Tuesday with nothing else painted read "Closed all day"): every slot
  // being closed is not enough. Unpainted time is open play (BOOKINGS_PLAN.md B3: "Outside every band, every area is open
  // play"), so the closed slots must also run without a gap from 0:00 to 24:00 (rows past midnight keep counting).
  const real = [...slotsByRow(day, groupId)].filter(([, x]) => !x.openPlay);
  if (!real.length) return false;
  if (!real.every(([, x]) => x.kind === 'Closed' || ((x.areas ?? []).length > 0 && (x.areas ?? []).every((c) => c.status === 'closed')))) return false;
  let reach = 0;
  for (const [start, x] of real) {
    if (start > reach) return false;
    reach = Math.max(reach, start + Math.max(1, Math.round((new Date(x.endsAt) - new Date(x.startsAt)) / 60000)));
  }
  return reach >= 1440;
}

/**
 * X202 (R82 L12): the API's day note when it says something the grid can't show: a part-day Closed band with the rest of
 * the day open play (X194, "Closed 6:00–14:00 · open play the rest of the day"). The grid draws the closed rows but not
 * the unpainted time, so /book and the admin calendar show this one line over the grid. Any other note stays off (X88:
 * the grid is the hours line).
 */
export const REST_OPEN_PLAY = 'open play the rest of the day';
export function restOpenPlayNote(note) {
  return typeof note === 'string' && note.includes(REST_OPEN_PLAY) ? note : '';
}

/** X202: part of the day has no band (open play, BOOKINGS_PLAN.md B3): an open-play slot, or a gap in 0:00–24:00. */
export function hasUnpaintedTime(day, groupId) {
  const rows = [...slotsByRow(day, groupId)];
  if (rows.some(([, x]) => x.openPlay)) return true;
  let reach = 0;
  for (const [start, x] of rows) {
    if (start > reach) return true;
    reach = Math.max(reach, start + Math.max(1, Math.round((new Date(x.endsAt) - new Date(x.startsAt)) / 60000)));
  }
  return reach < 1440;
}

/** "Not yet · opens Fri" for a day beyond the window. */
export function notYetWord(iso) {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return 'Not yet';
  return `Not yet · opens ${d.toLocaleDateString('en-GB', {weekday: 'short'})}`;
}

/**
 * X89 (R33 F4): the word under a day on a phone (a 64px button): "Sign in" for
 * "Sign in to book", "Opens Fri" for "Not yet · opens Fri". Null when the full
 * word already fits ("Full", "At limit", "Open play"). The day's marker
 * icons sit in the button's corner there, so the word has the whole width.
 * X91 (Grant 2026-10-21: "On mobile we can get rid of the word available and
 * use an icon instead"): a count is the number alone ("14", after the icon of
 * phoneDayIcon), and "Can't book" is the lock icon alone ("").
 */
export function phoneDayWord(word, free = null) {
  if (free > 0) return `${free}`;
  if (!word) return null;
  if (word === "Can't book") return '';
  // X229: "Done for today" lives in the tooltip and label only (no bar, no word in the tile).
  if (word === DONE_FOR_TODAY) return '';
  if (word === 'Sign in to book') return 'Sign in';
  if (word.startsWith('Not yet · opens ')) return `Opens ${word.slice('Not yet · opens '.length)}`;
  return null;
}

/** X91: the icon a phone's day shows before its short word: a tick before a count, the cells' lock for "Can't book". */
export const PHONE_DAY_AVAILABLE_ICON = 'mdi-check-circle-outline';
export function phoneDayIcon(word, free = null) {
  if (free > 0) return PHONE_DAY_AVAILABLE_ICON;
  if (word === "Can't book") return CELL_STATES.noAccess.icon;
  return null;
}

/**
 * The Mon–Sun strip: one entry per day of the week shown, with the free
 * count for the tab (null for a day not loaded yet).
 *   [{date, top, num, long, today, selected, past, beyond, opensAt, free, freeWord}]
 */
export function dayStrip(monday, days, {todayDate = null, selectedDate = null, tab = null, ...ctx} = {}) {
  return weekDays(monday).map((date) => {
    const day = (days ?? []).find((d) => d.date === date) ?? null;
    const h = dayHeader(date, todayDate);
    const past = !!todayDate && date < todayDate;
    // X91: the admin calendar counts what anyone could book (ctx.capacity), /book what this viewer can.
    // X229: both count only slots still to come (dayFreeCount through cellModel's started check, dayCapacity with now).
    const free = day && tab && !past ? (ctx.capacity ? dayCapacity(day, tab, ctx.now ?? null) : dayFreeCount(day, tab, ctx)) : null;
    // X204 (R83 L6): "Opens Thu" only on a day with something to book: a known day of these rules with no Booking needed
    // hours (open play, matches only, closed) opens nothing later, so it reads as itself (Open play …), never Opens.
    const beyond = !!day?.beyondWindow && !nothingToBook(day, tab);
    // X88 (Grant 2026-10-20): the hours line over the grid is gone (the grid shows the hours); what it alone said, that
    // the club changed this day's hours, is a marker on the day instead.
    const changed = dayChanged(day, tab, ctx.changedDates, date);
    // X195: the availability bar under the date (utils/bookingDayMeter), the words in the tooltip.
    const total = day && tab && !past ? dayTotal(day, tab, ctx.now ?? null) : 0;
    const word = past || beyond || free == null || free > 0 ? '' : zeroWord(day, tab, ctx);
    return {
      total, meter: dayMeter({free, total, word, past, beyond}),
      // Grant 2026-11-02: the day's data hasn't arrived yet (not a known day with nothing under it): the strip draws a
      // skeleton bar where the count goes, never a day word, until the week is here. A past day says nothing either way.
      pending: !day && !past,
      date, top: h.top, num: h.num, long: h.long, today: h.today, selected: date === selectedDate, past, beyond, changed, opensAt: day?.opensAt ?? null, free,
      // X91: a past day says nothing under its number; it is faded (.bds-day--past).
      freeWord: past ? '' : beyond ? notYetWord(day?.opensAt) : free == null ? '' : free === 0 ? zeroWord(day, tab, ctx) : `${free} available`,
      // X88/X89: a phone's day button is too narrow for the long words; each has a phone form; see phoneDayWord.
      freeShort: phoneDayWord(past ? '' : beyond ? notYetWord(day?.opensAt) : free == null ? '' : free === 0 ? zeroWord(day, tab, ctx) : `${free} available`, past || beyond ? 0 : free),
      // X91: on a phone the count is an icon and the number ("14 available" is its label and tooltip).
      phoneIcon: phoneDayIcon(past ? '' : beyond ? notYetWord(day?.opensAt) : free == null ? '' : free === 0 ? zeroWord(day, tab, ctx) : `${free} available`, past || beyond ? 0 : free),
    };
  });
}

/**
 * X204 (R83 L6): the day's slots for these rules are known (the API sent this group's day) and none is Booking needed hours
 * on a playing area that can be booked: nothing on it opens for booking later.
 */
export function nothingToBook(day, tab) {
  if (tab?.all) { const subs = tabsOf(tab); return subs.length > 0 && subs.every((t) => nothingToBook(day, t)); }
  const group = day?.groups?.find((g) => g.groupId === tab?.id);
  if (!group) return false;
  return !(group.slots ?? []).some((s) => !s.openPlay && s.kind === 'BookingNeeded'
      && (s.areas ?? []).some((c) => !['closed', 'open', 'matchesOnly'].includes(c.status)));
}

/**
 * X88: the club changed the hours of this day (a day change on the tab's rules, or on any rules with no tab; the
 * API's day line ends "· Changed for this day" otherwise). `changedDates` names days known changed from elsewhere
 * (the admin calendar loads one day and passes its own).
 */
export function dayChanged(day, tab = null, changedDates = null, date = null) {
  const on = day?.date ?? date;
  if (on && changedDates?.has?.(on)) return true;
  if (!day) return false;
  if (tab?.all) return tabsOf(tab).some((t) => dayChanged(day, t, changedDates, date));
  const groups = tab?.id ? (day.groups ?? []).filter((g) => g.groupId === tab.id) : (day.groups ?? []);
  if (groups.some((g) => (g.overrides ?? []).length > 0)) return true;
  const note = groups.length && tab?.id ? groups[0].note : day.note;
  return typeof note === 'string' && note.endsWith('Changed for this day');
}

/** The address: ?date= (the day shown; its Mon–Sun is the strip) and ?group=; defaults are left out. */
export function bookQuery(query, {date = null, groupId = null, todayDate = null, firstGroupId = null} = {}) {
  const next = {...(query ?? {})};
  const set = (k, v) => { if (v == null || v === '') delete next[k]; else next[k] = v; };
  set('date', date === todayDate ? null : date);
  set('group', groupId === firstGroupId ? null : groupId);
  delete next.area;
  return next;
}

// ── X88: the admin calendar's week strip (the /book strip with past weeks and no limit ahead) ──

/** The admin calendar's address for a day: ?date= (left out for today), every other key kept (?tab=, ?group=…). */
export function calendarDayQuery(query, date, todayDate) {
  const next = {...(query ?? {})};
  if (!date || date === todayDate) delete next.date; else next.date = date;
  return next;
}

/** Paging to another week keeps the weekday shown (Thu → the Thursday of that week); null (Today) is today. */
export function calendarWeekDate(date, toMonday, todayDate) {
  if (!toMonday) return todayDate;
  const from = weekStart(date);
  if (!from) return toMonday;
  const dow = Math.round((parse(date) - parse(from)) / 86400000);
  return addDays(weekStart(toMonday), dow);
}

/**
 * The admin strip: the Mon–Sun of the day shown. X91 (Grant 2026-10-21: the same strip features on both pages): with
 * the week's availability (`dayList`) and the tab, each day carries its count as /book's does ("14 available", "14
 * left" on a phone), counting every free bookable cell (an admin books for anyone); a past day faded, with no word.
 */
export function calendarStrip(date, todayDate, {changed = false, dayList = [], tab = null, now = null} = {}) {
  const monday = weekStart(date);
  return {
    monday,
    // X229: `now` (the calendar's minute clock) so today's slots that have started stop counting.
    days: dayStrip(monday, dayList ?? [], {todayDate, selectedDate: date, tab, capacity: true, now, changedDates: changed ? new Set([date]) : null}),
    label: weekLabel(monday),
    nav: weekNav(monday, todayDate, null, {pastWeeks: true, noLimitAhead: true}),
  };
}

export function sameQuery(a, b) {
  const ka = Object.keys(a ?? {}).sort(), kb = Object.keys(b ?? {}).sort();
  return ka.length === kb.length && ka.every((k, i) => k === kb[i] && String(a[k]) === String(b[k]));
}

/**
 * Grant 2026-11-02 (the club page's availability card, web and app: "This area could just show a green availability
 * percentage bar like on /book with a summary of court availability for today across all booking rules"): what is left
 * of today across EVERY booking rule, as /book's day meter counts it (anyone's free cells in slots still to come, of
 * every bookable cell still to come). Returns {free, total, fill (0..1, the real share free), level, line}; level is
 * the day meter's ('plenty' success, 'few' warning, 'full' error) or 'none' when nothing is left to book today.
 */
export function todayAvailability(availability, now = null) {
  const day = availability?.dayList?.[0] ?? null;
  let free = 0;
  let total = 0;
  for (const tab of groupTabs(availability)) {
    free += dayCapacity(day, tab, now);
    total += dayTotal(day, tab, now);
  }
  if (total === 0) return {free: 0, total: 0, fill: 0, level: 'none', line: 'Nothing left to book today'};
  const meter = dayMeter({free, total, word: free === 0 ? 'Full' : ''});
  const line = free === 0 ? 'Fully booked for the rest of today' : `${free} of ${total} ${total === 1 ? 'slot' : 'slots'} free today`;
  return {free, total, fill: free / total, level: meter?.level ?? 'full', line};
}
